<script setup>
/**
 * @fileoverview ConfirmDialog - Reusable confirmation modal dialog
 * @description Modal dialog for confirming actions with variant styling
 * @example
 * <ConfirmDialog
 *   v-model:open="isOpen"
 *   title="Delete Lab"
 *   description="Are you sure you want to delete this lab?"
 *   variant="danger"
 *   confirmLabel="Delete"
 *   :loading="isDeleting"
 *   @confirm="handleDelete"
 * />
 */
import { computed } from 'vue'
import { AlertTriangle, AlertCircle } from 'lucide-vue-next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui'
import { Button } from '@/components/ui'

const props = defineProps({
  /**
   * Dialog open state (v-model)
   * @type {boolean}
   */
  open: {
    type: Boolean,
    required: true
  },
  /**
   * Dialog title
   * @type {string}
   */
  title: {
    type: String,
    default: 'Confirm Action'
  },
  /**
   * Description text
   * @type {string}
   */
  description: {
    type: String,
    default: ''
  },
  /**
   * Confirm button text
   * @type {string}
   */
  confirmLabel: {
    type: String,
    default: 'Confirm'
  },
  /**
   * Cancel button text
   * @type {string}
   */
  cancelLabel: {
    type: String,
    default: 'Cancel'
  },
  /**
   * Dialog variant
   * @type {'default' | 'danger' | 'warning'}
   */
  variant: {
    type: String,
    default: 'default',
    validator: value => ['default', 'danger', 'warning'].includes(value)
  },
  /**
   * Show loading state
   * @type {boolean}
   */
  loading: {
    type: Boolean,
    default: false
  },
  /**
   * Optional icon to display
   * @type {Component}
   */
  icon: {
    type: Object,
    default: null
  }
})

const emit = defineEmits([
  /**
   * Update open state
   * @event update:open
   * @param {boolean} value
   */
  'update:open',
  /**
   * Confirm button clicked
   * @event confirm
   */
  'confirm',
  /**
   * Cancel button clicked or dialog closed
   * @event cancel
   */
  'cancel'
])

/**
 * Computed icon based on variant
 * @type {import('vue').ComputedRef<Component | null>}
 */
const displayIcon = computed(() => {
  if (props.icon) {
    return props.icon
  }
  if (props.variant === 'danger') {
    return AlertTriangle
  }
  if (props.variant === 'warning') {
    return AlertCircle
  }
  return null
})

/**
 * Icon color class based on variant
 * @type {import('vue').ComputedRef<string>}
 */
const iconColorClass = computed(() => {
  if (props.variant === 'danger') {
    return 'text-red-600 dark:text-red-500'
  }
  if (props.variant === 'warning') {
    return 'text-amber-600 dark:text-amber-500'
  }
  return 'text-slate-600 dark:text-slate-400'
})

/**
 * Confirm button variant based on dialog variant
 * @type {import('vue').ComputedRef<string>}
 */
const confirmButtonVariant = computed(() => {
  if (props.variant === 'danger') {
    return 'destructive'
  }
  if (props.variant === 'warning') {
    return 'default'
  }
  return 'default'
})

/**
 * Handle dialog open state change
 * @param {boolean} value
 */
const handleOpenChange = value => {
  emit('update:open', value)
  if (!value) {
    emit('cancel')
  }
}

/**
 * Handle confirm button click
 */
const handleConfirm = () => {
  emit('confirm')
}

/**
 * Handle cancel button click
 */
const handleCancel = () => {
  emit('update:open', false)
  emit('cancel')
}
</script>

<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <!-- Icon -->
        <div v-if="displayIcon" class="flex justify-center mb-4">
          <component :is="displayIcon" :class="['w-12 h-12', iconColorClass]" :stroke-width="1.5" />
        </div>

        <DialogTitle class="text-center">
          {{ title }}
        </DialogTitle>

        <DialogDescription v-if="description" class="text-center">
          {{ description }}
        </DialogDescription>
      </DialogHeader>

      <DialogFooter class="flex-col sm:flex-row gap-2">
        <Button variant="outline" :disabled="loading" @click="handleCancel">
          {{ cancelLabel }}
        </Button>

        <Button
          :variant="confirmButtonVariant"
          :disabled="loading"
          @click="handleConfirm"
          :aria-label="loading ? `${confirmLabel} - Processing` : confirmLabel"
        >
          <span v-if="loading" class="mr-2" aria-hidden="true">
            <svg
              class="animate-spin w-4 h-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          {{ confirmLabel }}
          <span v-if="loading" class="sr-only">Processing</span>
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
